import PropTypes from 'prop-types';
import React, { useContext, useRef } from 'react';
import { Box, Copy, Optional } from '@honda-canada/design-system-react';
import { useTranslation } from 'react-i18next';

import DealerAccordion from '../DealerAccordion';
import DealerCardsList from '../DealerCardsList';
import DealerContactQuickLinks from '../DealerContactQuickLinks/DealerContactQuickLinks';
import DealerDetailsModal from '../DealerDetailsModal';
import DealerNoResultAlert from '../DealerCardsList/DealerNoResultAlert';
import DepartmentTabs from '../DepartmentTabs';
import DraggableDealerDetails from './DraggableDealerDetails';
import EmbeddedGoogleMap from '../EmbeddedGoogleMap/index';
import themeStyles from './MobileLayout.styles';
import useConfiguration from '../../hooks/useConfiguration';
import useJSSComponentContext from '../../hooks/useJSSComponentContext';
import { DealersListContext } from '../DealersListContext';
import { LIST_VIEW_IDENTIFIER, MAP_VIEW_IDENTIFIER } from '../../utils/constants';

const ViewContainerMap = themeStyles.apply(Box, 'ViewContainerMap');
const ViewContainerList = themeStyles.apply(Box, 'ViewContainerList');
const ToggleButton = themeStyles.apply(Copy, 'ToggleButton');

const ToggleViewContainer = ({ isFullScreenModal, setFullScreenModal, variant, disclaimerHeight }) => {
  const dealerCardsListRef = useRef(null);
  // FIXME: This is only meant to be used for InventoryVariant
  const { componentName: isInventoryVariant } = useJSSComponentContext();

  const { setActiveView, activeView, selectedDealer, dealersList, selectedPlace, isDealerModalOpen } =
    useContext(DealersListContext);
  const { showQuickLinks } = useConfiguration();

  const mapViewClickHandler = () => {
    setActiveView(MAP_VIEW_IDENTIFIER);
  };

  const { t } = useTranslation();

  if (activeView === LIST_VIEW_IDENTIFIER) {
    const height = `calc(100dvh - ${disclaimerHeight}px)`;
    return (
      <ViewContainerList isFullScreenModal={isDealerModalOpen} height={height} data-testid="cy-dealer-cards-list">
        <DealerCardsList dealerCardsListRef={dealerCardsListRef} />
        <ToggleButton isHidden={isDealerModalOpen} data-testid="cy-mapview-cta" onClick={mapViewClickHandler}>
          {t('Sub-apps.Dealers.showMapViewButton')}
        </ToggleButton>
        <DealerDetailsModal dealer={selectedDealer} withoutArrow>
          <DepartmentTabs dealer={selectedDealer} variant={variant} />
          <Optional when={!showQuickLinks}>
            <DealerAccordion productLines={selectedDealer?.productLines} />
          </Optional>
          <Optional when={showQuickLinks}>
            <DealerContactQuickLinks
              dealerCode={selectedDealer?.code}
              serviceBookingUrl={selectedDealer?.serviceBookingUrl}
            />
          </Optional>
        </DealerDetailsModal>
      </ViewContainerList>
    );
  }

  const showToggle =
    (isInventoryVariant || !selectedDealer) && activeView === MAP_VIEW_IDENTIFIER && dealersList.length > 0;

  return (
    <ViewContainerMap isFullScreenModal={isFullScreenModal} data-testid="cy-mapview">
      <EmbeddedGoogleMap width="100%" />
      <Optional when={!isInventoryVariant && selectedDealer}>
        <DraggableDealerDetails
          isFullScreenModal={isFullScreenModal}
          setFullScreenModal={setFullScreenModal}
          activeView={activeView}
          variant={variant}
        />
      </Optional>
      <Optional when={showToggle}>
        <ToggleButton data-testid="cy-listview-cta" onClick={() => setActiveView(LIST_VIEW_IDENTIFIER)}>
          {t('Sub-apps.Dealers.showListViewButton')}
        </ToggleButton>
      </Optional>
      {dealersList.length < 0 && selectedPlace && <DealerNoResultAlert />}
    </ViewContainerMap>
  );
};

ToggleViewContainer.propTypes = {
  isFullScreenModal: PropTypes.bool,
  disclaimerHeight: PropTypes.string,
  setFullScreenModal: PropTypes.func,
  variant: PropTypes.string,
};

export default ToggleViewContainer;
